import {
    IForEachActivityInterimContext,
    IIfConditionActivityInterimContext,
    IPipelineCopilotContext,
    IPipelineCopilotCurrentPipelineContext,
    IUntilActivityInterimContext,
    PipelineActivityContext,
    PipelineActivityInterimContext,
    IUpdatePipelinePayload,
    ISwitchActivityInterimContext,
    ISwitchCaseInterimContext,
    IErrorItemContext,
    CopilotEntityProperties,
    CopilotChatMessage,
    IRunContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { AdapterContext, arrayIsNullOrEmpty, IResourceContext, Metamodel, Pipeline } from 'shell-models-lib';
import { Model } from 'app/model/backendmodel';
import { IIFrameResourceActionBaseData } from 'app/interfaces/IIFrameMessage';
import { isEmpty, omit, remove } from 'lodash';
import { calcDocLink } from 'app/shared/utils/activity-run-troubleshooting-doc.util';
import { IActivityRun, IPipelineRun } from 'app/model/monitoring/monitoring-models';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { CopyJob } from 'app/model/copyjob/copy-job';
import { TridentGetPipelineCopilotResponseParams } from 'app/interfaces/TridentApiServiceDefinitions';
import { FabricCertifiedEvent, FabricFeatureName } from 'app/logger/logger.fabric-certificied.event';
import { LoggerService } from 'app/logger/logger.service';

export async function generateCopilotContextFromPipelineModelAsync(
    pipeline: Pipeline,
    data: IIFrameResourceActionBaseData,
    selectedRun?: IPipelineRun,
    error?: IErrorItemContext
): Promise<IPipelineCopilotContext> {
    const result = await AdapterContext.defaultContextOf(data.contextData)
        .asyncAdapterFor<
            Pipeline,
            Model.PipelineResource,
            IPipelineCopilotCurrentPipelineContext
        >(Metamodel.modelFor(Pipeline))
        .toFabricCopilotContext(pipeline);

    if (!arrayIsNullOrEmpty(result?.Activities)) {
        // convert interim context to final context
        const activityContexts: PipelineActivityContext[] = [];
        convertInterimContextToFinalContext(result.Activities, activityContexts);
        result.Activities = activityContexts;
    }
    let runs: IRunContext[];
    if (data.contextData.contextType === 'mountedDataFactory') {
        // Mounted explanations must not fall back to another pipeline's cached runs.
        runs = [];
        if (error) {
            if (!selectedRun?.runId) {
                throw new Error('Mounted data factory error explanation requires the selected pipeline run.');
            }
            // ADF monitoring preserves ISO timestamp strings despite the Date-typed run model.
            runs = [{
                Id: selectedRun.runId,
                Status: (selectedRun.originalStatus ?? selectedRun.status) === 'Succeeded' ? 'Success' : 'Fail',
                StartTime: selectedRun.runStart != null ? new Date(selectedRun.runStart).toISOString() : undefined,
                EndTime: selectedRun.runEnd != null ? new Date(selectedRun.runEnd).toISOString() : undefined,
                Errors: [error]
            }];
        }
    } else {
        runs = await fetchRunsForPipeline(data.contextData, selectedRun, error);
    }
    return {
        Pipeline: result,
        Runs: runs,
    };
}

export async function fetchRunsForPipeline(contextData: IResourceContext, selectedRun?: IPipelineRun, error?: IErrorItemContext): Promise<IRunContext[]> {
    try {
        if (!selectedRun) {
            const pipelineRunProvider: {data: IPipelineRun[]} = await getServiceInstance(contextData, 'PipelineRunDataProvider');
            if (!pipelineRunProvider || !pipelineRunProvider.data || pipelineRunProvider.data.length === 0) {
                return [];
            }
            selectedRun = pipelineRunProvider.data[pipelineRunProvider.data.length - 1];
        }
        
        const runContext: IRunContext = {
            Id: selectedRun.runId,
            Status: selectedRun.status === 'Succeeded' ? 'Success' : 'Fail',
            StartTime: selectedRun.startTime,
            EndTime: selectedRun.endTime,
            Errors: error ? [error] : await fetchErrorsForRun(contextData)
        };

        return [runContext];
    } catch (e) {
        return [];
    }
}

async function fetchErrorsForRun(contextData: IResourceContext): Promise<IErrorItemContext[]> {
    try {
        const activityRunProvider: { data: IActivityRun[] } = await getServiceInstance(contextData, 'ActivityRunDataProvider');
        if (!activityRunProvider) {
            return [];
        }
        const failedRuns = activityRunProvider.data?.filter((run: IActivityRun) => run.status === 'Failed');
        if (!failedRuns || failedRuns.length === 0) {
            return [];
        }
        const sortedFailures = failedRuns.sort((a, b) => new Date(b.runEnd).getTime() - new Date(a.runEnd).getTime());
        const errorItems = sortedFailures.map((run) => convertActivityRunToErrorItem(run));
        const groupedErrors = groupSimilarErrorItems(errorItems, sortedFailures);
        return groupedErrors;
    } catch (e) {
        return [];
    }

// Helper function to group similar error items
function groupSimilarErrorItems(errorItems: IErrorItemContext[], runs: IActivityRun[]): IErrorItemContext[] {
    const groups: Array<{ error: IErrorItemContext; activityNames: string[] }> = [];
    for (let i = 0; i < errorItems.length; i++) {
        const errorItem = errorItems[i];
        const run = runs[i];
        const messageWordCount = errorItem.Message.split(/\s+/).length;
        let foundGroup = false;
        for (const group of groups) {
            const rootError = group.error;
            const rootMessageWordCount = rootError.Message.split(/\s+/).length;
            if (
                errorItem.ActivityType === rootError.ActivityType &&
                errorItem.Code === rootError.Code &&
                Math.abs(messageWordCount - rootMessageWordCount) <= 5
            ) {
                if (!group.activityNames.includes(run.activityName)) {
                    group.activityNames.push(run.activityName);
                }
                foundGroup = true;
                break;
            }
        }
        if (!foundGroup && groups.length <= 10) {
            groups.push({ error: errorItem, activityNames: [run.activityName] });
        }
    }
    // Map to IErrorItemContext with flattened activity names
    return groups.map((group) => ({
        ...group.error,
        ActivityNames: group.activityNames
    }));
}
}

export function convertActivityRunToErrorItem(run: IActivityRun): IErrorItemContext {
    let errorCode, message, admsErrorCodeForDocLink, failureType, docLink: string;
    try {
        const jsonError = JSON.parse(run.error);
        errorCode = jsonError.errorCode;
        message = jsonError.message;
        failureType = jsonError.failureType;
        if (
            jsonError.message &&
            jsonError.message.match(/ErrorCode=(\w+)/) &&
            jsonError.message.match(/ErrorCode=(\w+)/)[1]
        ) {
            admsErrorCodeForDocLink = jsonError.message.match(/ErrorCode=(\w+)/)[1];
        }

        if (run.activityType === 'ExecuteDataFlow') {
            const jsonMessage = JSON.parse(jsonError.message);
            errorCode = jsonMessage.StatusCode;
            jsonError.errorCode = errorCode;
            message =
                (jsonMessage.Message as string)?.length < 500
                    ? jsonMessage.Message +
                    '. Details:' +
                    (jsonMessage.Details as string)?.substring(
                        0,
                        500 - (jsonMessage.Message as string)?.length
                    )
                    : (jsonMessage.Message as string)?.substring(0, 500);
        }
        docLink = calcDocLink(run.activityType, errorCode, admsErrorCodeForDocLink) || 'https://learn.microsoft.com/fabric/data-factory/pipeline-troubleshoot-guide';
    } catch (e) {
        return {
            Message: message?.substring(0, 650) || 'Unknown error',
            Type: failureType || 'Unknown error',
            Code: errorCode || 'Unknown error',
            ActivityNames:  [run.activityName],
            ActivityType: run.activityType
        }
    }
    return {
        Message: message.substring(0, 650),
        Type: failureType,
        Code: errorCode,
        ActivityNames: [run.activityName],
        ActivityType: run.activityType,
        DocLink: docLink
    }
}

export async function updatePipelineModelFromCopilotResponseAsync(
    data: IUpdatePipelinePayload,
    pipeline: Pipeline,
    contextData: IResourceContext
) {
    const pipelineContext: IPipelineCopilotCurrentPipelineContext<PipelineActivityContext[]> = data.pipelineContext;
    if (!arrayIsNullOrEmpty(data.payload)) {
        // 1. apply update payload to pipeline context
        for (const item of data.payload) {
            switch (item.Type) {
                case 'AddActivity': {
                    pipelineContext.Activities.push(item.Activity);
                    break;
                }
                case 'UpdateActivity': {
                    const existingActivityIndex = pipelineContext.Activities.findIndex(
                        (activity) => activity.Name === item.Activity.Name && activity.Type === item.Activity.Type
                    );
                    if (existingActivityIndex != -1) {
                        pipelineContext.Activities.splice(existingActivityIndex, 1, item.Activity);
                    }
                    break;
                }
                case 'RemoveActivity': {
                    const activityIndex = pipelineContext.Activities.findIndex(
                        (activity) => activity.Name === item.Activity.Name && activity.Type === item.Activity.Type
                    );
                    if (activityIndex !== -1) {
                        const removedActivity = pipelineContext.Activities.splice(activityIndex, 1)[0];
                        removedActivity.Dependencies.forEach((dependency) => {
                            const dependencyIndex = pipelineContext.Activities.findIndex((activity) => activity.Name === dependency.ActivityName);
                            if (dependencyIndex !== -1) {
                                const activity = pipelineContext.Activities[dependencyIndex];
                                activity.Dependencies = activity.Dependencies.filter((dep) => dep.ActivityName !== removedActivity.Name);
                            }
                        });
                    }
                    break;
                }
                case 'AddParameter': {
                    pipelineContext.Parameters.push(item.Parameter);
                    break;
                }
                case 'UpdateParameter': {
                    const existingParameterIndex = pipelineContext.Parameters.findIndex(
                        (parameter) => parameter.Name === item.Parameter.Name
                    );
                    if (existingParameterIndex != -1) {
                        pipelineContext.Parameters.splice(existingParameterIndex, 1, item.Parameter);
                    }
                    break;
                }
                case 'RemoveParameter': {
                    pipelineContext.Parameters = pipelineContext.Parameters.filter(
                        (parameter) => parameter.Name !== item.Parameter.Name && parameter.Type !== item.Parameter.Type
                    );
                    break;
                }
                case 'AddVariable': {
                    pipelineContext.Variables.push(item.Variable);
                    break;
                }
                case 'UpdateVariable': {
                    const existingVariableIndex = pipelineContext.Variables.findIndex(
                        (variable) => variable.Name === item.Variable.Name
                    );
                    if (existingVariableIndex != -1) {
                        pipelineContext.Variables.splice(existingVariableIndex, 1, item.Variable);
                    }
                    break;
                }
                case 'RemoveVariable': {
                    pipelineContext.Variables = pipelineContext.Variables.filter(
                        (variable) => variable.Name !== item.Variable.Name && variable.Type !== item.Variable.Type
                    );
                    break;
                }
                default:
                    break;
            }
        }
        // 2. convert pipeline context to interim context
        if (!arrayIsNullOrEmpty(pipelineContext.Activities)) {
            const activityContexts: PipelineActivityInterimContext[] = [];
            const activityContextNamesToRemove: string[] = [];
            const topLevelActivityContexts = pipelineContext.Activities.slice();
            getNestedActivityContextsToRemove(pipelineContext.Activities, activityContextNamesToRemove);
            remove(topLevelActivityContexts, (item) => activityContextNamesToRemove.includes(item.Name));
            convertFinalContextToInterimContext(topLevelActivityContexts, pipelineContext.Activities, activityContexts);
            pipelineContext.Activities = activityContexts;
        }
        // 3. update pipeline model
        const pipelineAdapter = AdapterContext.defaultContextOf(contextData).asyncAdapterFor<
            Pipeline,
            Model.PipelineResource,
            IPipelineCopilotCurrentPipelineContext
        >(Pipeline.MODEL);
        await pipelineAdapter.fromFabricCopilotContext(pipelineContext, pipeline);
    }
}

function convertInterimContextToFinalContext(
    activityInterimContexts: PipelineActivityInterimContext[],
    activityContexts: PipelineActivityContext[]
) {
    for (const interimContext of activityInterimContexts ?? []) {
        if (isInterimActivityContext(interimContext, 'ForEach') || isInterimActivityContext(interimContext, 'Until')) {
            activityContexts.push({
                ...omit(interimContext, 'ChildActivities'),
                ChildActivityNames: interimContext.ChildActivities?.map((item) => item.Name) ?? []
            });
            convertInterimContextToFinalContext(interimContext.ChildActivities, activityContexts);
        } else if (isInterimActivityContext(interimContext, 'IfCondition')) {
            activityContexts.push({
                ...omit(interimContext, 'TrueChildActivities', 'FalseChildActivities'),
                TrueChildActivityNames: interimContext.TrueChildActivities?.map((item) => item.Name) ?? [],
                FalseChildActivityNames: interimContext.FalseChildActivities?.map((item) => item.Name) ?? []
            });
            convertInterimContextToFinalContext(interimContext.TrueChildActivities, activityContexts);
            convertInterimContextToFinalContext(interimContext.FalseChildActivities, activityContexts);
        } else if (isInterimActivityContext(interimContext, 'Switch')) {
            activityContexts.push({
                ...omit(interimContext, 'Cases', 'DefaultChildActivities'),
                DefaultChildActivityNames: interimContext.DefaultChildActivities?.map((item) => item.Name) ?? [],
                Cases:
                    interimContext.Cases?.map((caseItem) => ({
                        ExpressionReturnValue: caseItem.ExpressionReturnValue,
                        ChildActivityNames: caseItem.ChildActivities?.map((activity) => activity.Name) ?? []
                    })) ?? []
            });
            convertInterimContextToFinalContext(interimContext.DefaultChildActivities, activityContexts);
            for (const caseItem of interimContext.Cases ?? []) {
                convertInterimContextToFinalContext(caseItem.ChildActivities, activityContexts);
            }
        } else {
            activityContexts.push(interimContext);
        }
    }
}

function convertFinalContextToInterimContext(
    topLevelFinalActivityContexts: PipelineActivityContext[],
    fullFinalActivityContexts: PipelineActivityContext[],
    activityInterimContexts: PipelineActivityInterimContext[]
) {
    for (const finalContext of topLevelFinalActivityContexts ?? []) {
        if (isFinalActivityContext(finalContext, 'ForEach') || isFinalActivityContext(finalContext, 'Until')) {
            const result: IForEachActivityInterimContext | IUntilActivityInterimContext = {
                ...omit(finalContext, 'ChildActivityNames'),
                ChildActivities: []
            };
            for (const childActivityName of finalContext.ChildActivityNames ?? []) {
                const childActivityPayload = fullFinalActivityContexts.find((item) => item.Name === childActivityName);
                if (childActivityPayload) {
                    if (result.ChildActivities == null) {
                        result.ChildActivities = [];
                    }
                    convertFinalContextToInterimContext(
                        [childActivityPayload],
                        fullFinalActivityContexts,
                        result.ChildActivities
                    );
                }
            }
            activityInterimContexts.push(result);
        } else if (isFinalActivityContext(finalContext, 'IfCondition')) {
            const result: IIfConditionActivityInterimContext = {
                ...omit(finalContext, 'TrueChildActivityNames', 'FalseChildActivityNames'),
                TrueChildActivities: [],
                FalseChildActivities: []
            };
            for (const childActivityName of finalContext.TrueChildActivityNames ?? []) {
                const childActivityPayload = fullFinalActivityContexts.find((item) => item.Name === childActivityName);
                if (childActivityPayload) {
                    if (result.TrueChildActivities == null) {
                        result.TrueChildActivities = [];
                    }
                    convertFinalContextToInterimContext(
                        [childActivityPayload],
                        fullFinalActivityContexts,
                        result.TrueChildActivities
                    );
                }
            }
            for (const childActivityName of finalContext.FalseChildActivityNames ?? []) {
                const childActivityPayload = fullFinalActivityContexts.find((item) => item.Name === childActivityName);
                if (childActivityPayload) {
                    if (result.FalseChildActivities == null) {
                        result.FalseChildActivities = [];
                    }
                    convertFinalContextToInterimContext(
                        [childActivityPayload],
                        fullFinalActivityContexts,
                        result.FalseChildActivities
                    );
                }
            }
            activityInterimContexts.push(result);
        } else if (isFinalActivityContext(finalContext, 'Switch')) {
            const result: ISwitchActivityInterimContext = {
                ...omit(finalContext, 'Cases', 'DefaultChildActivityNames'),
                DefaultChildActivities: [],
                Cases: []
            };
            for (const childActivityName of finalContext.DefaultChildActivityNames ?? []) {
                const childActivityPayload = fullFinalActivityContexts.find((item) => item.Name === childActivityName);
                if (childActivityPayload) {
                    if (result.DefaultChildActivities == null) {
                        result.DefaultChildActivities = [];
                    }
                    convertFinalContextToInterimContext(
                        [childActivityPayload],
                        fullFinalActivityContexts,
                        result.DefaultChildActivities
                    );
                }
            }
            for (const caseItem of finalContext.Cases ?? []) {
                const caseInterimContext: ISwitchCaseInterimContext = {
                    ExpressionReturnValue: caseItem.ExpressionReturnValue,
                    ChildActivities: []
                };
                for (const childActivityName of caseItem.ChildActivityNames ?? []) {
                    const childActivityPayload = fullFinalActivityContexts.find(
                        (item) => item.Name === childActivityName
                    );
                    if (childActivityPayload) {
                        if (caseInterimContext.ChildActivities == null) {
                            caseInterimContext.ChildActivities = [];
                        }
                        convertFinalContextToInterimContext(
                            [childActivityPayload],
                            fullFinalActivityContexts,
                            caseInterimContext.ChildActivities
                        );
                    }
                }
                result.Cases.push(caseInterimContext);
            }
            activityInterimContexts.push(result);
        } else {
            activityInterimContexts.push(finalContext);
        }
    }
}

function getNestedActivityContextsToRemove(
    activityFinalContexts: PipelineActivityContext[],
    activityContextNamesToRemove: string[]
) {
    for (const finalContext of activityFinalContexts ?? []) {
        if (isFinalActivityContext(finalContext, 'ForEach') || isFinalActivityContext(finalContext, 'Until')) {
            for (const childActivityName of finalContext.ChildActivityNames ?? []) {
                const childActivityPayload = activityFinalContexts.find((item) => item.Name === childActivityName);
                if (childActivityPayload) {
                    activityContextNamesToRemove.push(childActivityName);
                    getNestedActivityContextsToRemove([childActivityPayload], activityContextNamesToRemove);
                }
            }
        } else if (isFinalActivityContext(finalContext, 'IfCondition')) {
            for (const childActivityName of finalContext.TrueChildActivityNames ?? []) {
                const childActivityPayload = activityFinalContexts.find((item) => item.Name === childActivityName);
                if (childActivityPayload) {
                    activityContextNamesToRemove.push(childActivityName);
                    getNestedActivityContextsToRemove([childActivityPayload], activityContextNamesToRemove);
                }
            }
            for (const childActivityName of finalContext.FalseChildActivityNames ?? []) {
                const childActivityPayload = activityFinalContexts.find((item) => item.Name === childActivityName);
                if (childActivityPayload) {
                    activityContextNamesToRemove.push(childActivityName);
                    getNestedActivityContextsToRemove([childActivityPayload], activityContextNamesToRemove);
                }
            }
        } else if (isFinalActivityContext(finalContext, 'Switch')) {
            for (const childActivityName of finalContext.DefaultChildActivityNames ?? []) {
                const childActivityPayload = activityFinalContexts.find((item) => item.Name === childActivityName);
                if (childActivityPayload) {
                    activityContextNamesToRemove.push(childActivityName);
                    getNestedActivityContextsToRemove([childActivityPayload], activityContextNamesToRemove);
                }
            }
            for (const caseItem of finalContext.Cases ?? []) {
                for (const childActivityName of caseItem.ChildActivityNames ?? []) {
                    const childActivityPayload = activityFinalContexts.find((item) => item.Name === childActivityName);
                    if (childActivityPayload) {
                        activityContextNamesToRemove.push(childActivityName);
                        getNestedActivityContextsToRemove([childActivityPayload], activityContextNamesToRemove);
                    }
                }
            }
        } else {
            // Plain activity, do nothing
        }
    }
}

function isInterimActivityContext<T extends PipelineActivityInterimContext['Type']>(
    context: PipelineActivityInterimContext,
    type: T
): context is Extract<PipelineActivityInterimContext, { Type: T }> {
    return context.Type === type;
}

function isFinalActivityContext<T extends PipelineActivityContext['Type']>(
    context: PipelineActivityContext,
    type: T
): context is Extract<PipelineActivityContext, { Type: T }> {
    return context.Type === type;
}

export function convertEntityToCopilotEntityProperties(entity: Pipeline | CopyJob): CopilotEntityProperties {
    if (isEmpty(entity)) {
        throw new Error('Entity properties or entity must be provided');
    }

    return {
        fabricArtifactType: entity.extraTridentInfo.artifactType as TridentGetPipelineCopilotResponseParams['fabricArtifactType'],
        fabricArtifactObjectId: entity.objectId,
        fabricWorkspaceObjectId: entity.workspaceObjectId,
    };
}

export function isMeaningfulMessage(message: CopilotChatMessage){
    return !!message?.content && !message.details?.isFailed && !message.details?.isCanceled;
}

/** For experiment in Fabric. Can be removed once the experiment ends. */
export function logFabricExperiment({
    properties, logger
}: {
    properties: {
        featureName: FabricFeatureName;
        operationName: string;
        status?: string;
        startTime?: Date;
        endTime?: Date;
        activityAttributes?: Record<string, string | number | boolean>;
    }
    logger: LoggerService;
}): void {
    if (!__IS_FABRIC_DPE__) {
        return;
    }

    const row = new FabricCertifiedEvent(
        properties.featureName,
        properties.operationName,
        properties.status,
        properties.startTime,
        properties.endTime,
        properties.activityAttributes,
        { eventOptions: { logExpAssignment: true } }
    );
    logger.logEvent(row);
}
